import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PickerPlacement } from '../types'
import { PortalContainer } from '../utils/usePortal'
import { OverlayPosition, positionUtils } from './positionUtils'

export interface UsePositionParams {
  container?: PortalContainer
  placement: PickerPlacement
  preventOverflow: boolean
  triggerTarget: RefObject<HTMLElement>
  overlayRef: RefObject<HTMLElement>
}

interface UpdatePositionParams {
  placementChanged: boolean
  forceUpdateDOM: boolean
}

const PLACEMENT_CLASS_PATTERN = /(placement-\S+)/

const INITIAL_POSITION: OverlayPosition = { positionLeft: 0, positionTop: 0, positionClassName: '' }

interface ObserveResizeParams {
  element: HTMLElement
  onResize: () => void
}

const observeResize = ({ element, onResize }: ObserveResizeParams) => {
  let hasInitialSize = false
  const observer = new ResizeObserver(() => {
    if (hasInitialSize) {
      onResize()
    }
    hasInitialSize = true
  })
  observer.observe(element)
  return observer
}

export const usePosition = ({
  container,
  placement,
  preventOverflow,
  triggerTarget,
  overlayRef,
}: UsePositionParams) => {
  const containerRef = useRef<HTMLElement | null>(null)
  const lastTargetRef = useRef<HTMLElement | null>(null)
  const [position, setPosition] = useState(INITIAL_POSITION)
  const utils = useMemo(
    () => positionUtils({ placement, preventOverflow }),
    [placement, preventOverflow],
  )

  const updatePosition = useCallback(
    ({ placementChanged, forceUpdateDOM }: UpdatePositionParams) => {
      const target = triggerTarget.current
      const overlay = overlayRef.current

      if (!target || !overlay) {
        return
      }

      if (target === lastTargetRef.current && !placementChanged) {
        return
      }

      const containerElement = container?.() || overlay.ownerDocument.body
      const nextPosition = utils.calcOverlayPosition({
        overlay,
        target,
        container: containerElement,
      })

      if (forceUpdateDOM) {
        const previousClassName = overlay.className.match(PLACEMENT_CLASS_PATTERN)?.[0]
        if (previousClassName) {
          overlay.classList.remove(previousClassName)
        }
        overlay.classList.add(nextPosition.positionClassName)
        overlay.style.left = `${nextPosition.positionLeft}px`
        overlay.style.top = `${nextPosition.positionTop}px`
      } else {
        setPosition(nextPosition)
      }

      containerRef.current = containerElement
      lastTargetRef.current = target
    },
    [container, overlayRef, triggerTarget, utils],
  )

  useEffect(() => {
    updatePosition({ placementChanged: false, forceUpdateDOM: false })

    const forceUpdate = () => updatePosition({ placementChanged: true, forceUpdateDOM: true })
    const scrollContainer = containerRef.current
    const scrollTarget = scrollContainer?.tagName === 'BODY' ? window : scrollContainer
    const shouldTrackScroll = Boolean(scrollTarget && preventOverflow)

    if (shouldTrackScroll) {
      scrollTarget?.addEventListener('scroll', forceUpdate)
    }
    window.addEventListener('resize', forceUpdate)
    const overlay = overlayRef.current
    const resizeObserver = overlay
      ? observeResize({ element: overlay, onResize: forceUpdate })
      : null

    return () => {
      lastTargetRef.current = null
      if (shouldTrackScroll) {
        scrollTarget?.removeEventListener('scroll', forceUpdate)
      }
      window.removeEventListener('resize', forceUpdate)
      resizeObserver?.disconnect()
    }
  }, [preventOverflow, overlayRef, updatePosition])

  const isMountedRef = useRef(false)
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true
      return
    }
    updatePosition({ placementChanged: true, forceUpdateDOM: false })
  }, [updatePosition])

  return position
}
